import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff, AccountCircle, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { systemConfigAPI, authAPI } from '../services/api';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoPath, setLogoPath] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const [changePwOpen, setChangePwOpen] = useState(false);
  const [cpUsername, setCpUsername] = useState('');
  const [cpCurrentPassword, setCpCurrentPassword] = useState('');
  const [cpNewPassword, setCpNewPassword] = useState('');
  const [cpConfirmPassword, setCpConfirmPassword] = useState('');
  const [cpError, setCpError] = useState('');
  const [cpSuccess, setCpSuccess] = useState('');
  const [cpLoading, setCpLoading] = useState(false);

  const openChangePassword = () => {
    setCpUsername(username);
    setCpCurrentPassword('');
    setCpNewPassword('');
    setCpConfirmPassword('');
    setCpError('');
    setCpSuccess('');
    setChangePwOpen(true);
  };

  const closeChangePassword = () => {
    setChangePwOpen(false);
  };

  const handleChangePasswordSubmit = async () => {
    setCpError('');
    setCpSuccess('');

    if (!cpUsername.trim() || !cpCurrentPassword || !cpNewPassword || !cpConfirmPassword) {
      setCpError('All fields are required');
      return;
    }
    if (cpNewPassword.length < 6) {
      setCpError('New password must be at least 6 characters long');
      return;
    }
    if (cpNewPassword !== cpConfirmPassword) {
      setCpError('New passwords do not match');
      return;
    }
    if (cpCurrentPassword === cpNewPassword) {
      setCpError('New password must be different from the current password');
      return;
    }

    setCpLoading(true);
    try {
      await authAPI.changePasswordPublic(cpUsername.trim(), cpCurrentPassword, cpNewPassword);
      setCpSuccess('Password changed successfully! You can now sign in with your new password.');
      setTimeout(() => closeChangePassword(), 2000);
    } catch (err) {
      setCpError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setCpLoading(false);
    }
  };

  useEffect(() => {
    // Fetch logo - handle errors gracefully as login page is public
    systemConfigAPI.getCurrentLogo()
      .then(response => {
        if (response.data.logo_path) {
          setLogoPath(`${process.env.REACT_APP_API_BASE_URL}/${response.data.logo_path}`);
        }
      })
      .catch(err => {
        // Silently fail - logo is optional on login page
        console.log('Logo not available');
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQqDMNgCkWD08fprzoY2PCnksJO5BcfPAwtpw&s)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dark overlay for better readability
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <Paper 
          elevation={10} 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
          }}
        >
          {/* Logo */}
          {logoPath && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                component="img"
                src={logoPath}
                alt="ISKCON Logo"
                sx={{ 
                  height: 80, 
                  objectFit: 'contain',
                }}
                onError={() => setLogoPath('')}
              />
            </Box>
          )}

          {/* Title */}
          <Typography 
            component="h1" 
            variant="h4" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              color: 'primary.main',
              mb: 1,
            }}
          >
            Govinda Dham
          </Typography>
          
          <Typography 
            variant="h6" 
            align="center" 
            color="textSecondary" 
            gutterBottom
            sx={{ mb: 3 }}
          >
            ISKCON Hubli-Dharwad Applications
          </Typography>

          {/* Welcome Text */}
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Hare Krishna! Welcome. Please sign in to continue
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 90%)',
                  boxShadow: '0 5px 8px 3px rgba(255, 105, 135, .4)',
                },
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="text"
                size="small"
                onClick={openChangePassword}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Change Password
              </Button>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              © {new Date().getFullYear()} ISKCON Hubli-Dharwad. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* Change Password Dialog - self-service, no session required, but the
          caller must know their current password. Useful right after an
          admin resets someone to the default temporary password. */}
      <Dialog open={changePwOpen} onClose={closeChangePassword} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {cpError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cpError}
            </Alert>
          )}
          {cpSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {cpSuccess}
            </Alert>
          )}

          <TextField
            margin="normal"
            fullWidth
            label="Username"
            value={cpUsername}
            onChange={(e) => setCpUsername(e.target.value)}
            disabled={cpLoading}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Current Password"
            type="password"
            value={cpCurrentPassword}
            onChange={(e) => setCpCurrentPassword(e.target.value)}
            disabled={cpLoading}
            helperText="If an admin reset your password, use the temporary password they gave you"
          />

          <TextField
            margin="normal"
            fullWidth
            label="New Password"
            type="password"
            value={cpNewPassword}
            onChange={(e) => setCpNewPassword(e.target.value)}
            disabled={cpLoading}
            helperText="Must be at least 6 characters"
          />

          <TextField
            margin="normal"
            fullWidth
            label="Confirm New Password"
            type="password"
            value={cpConfirmPassword}
            onChange={(e) => setCpConfirmPassword(e.target.value)}
            disabled={cpLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeChangePassword} disabled={cpLoading}>
            Cancel
          </Button>
          <Button onClick={handleChangePasswordSubmit} variant="contained" disabled={cpLoading}>
            {cpLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
